import { CheckCircleFilled, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authActions } from 'reduxStore/auth/slice';
import { CreateOrganisationModal } from 'zComponents/molecules';
import { Button, Divider, Flex, Input, Tooltip, Typography } from 'zui/Antd';
import { SelectSecondary } from 'zui/Custom';

const { Title } = Typography;
const DropdownWorkspace = () => {
	//Hooks dispatch
	const dispatch = useDispatch();
	const {
		token: {
			colorPrimary,
			colorTextLabel,
			paddingSM,
			paddingXS,
			borderRadiusLG,
			padding,
			colorTextDescription,
			paddingXXS,
			colorBorderSecondary,
			fontWeightStrong
		}
	} = theme.useToken();

	//Redux state
	const { data: organisationData } = useSelector((state) => state.Organisations);
	const { selectedProfile } = useSelector((state) => state.Auth);

	//Local state
	const [searchText, setSearchText] = useState('');
	const [openCreateModal, setOpenCreateModal] = useState(false);
	const [isHovered, setIsHovered] = useState(false);

	//Permission check for adding an organisation
	const isPermittedCreateOrg = useManagePermittedRoles('addOrg');

	// Filter and map organisation data for the select dropdown options
	const filteredWorkspaces = useMemo(() => {
		return organisationData
			.filter((org) => org.name.toLowerCase().includes(searchText.toLowerCase()))
			.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
			.map((org) => ({
				value: String(org.orgID),
				label: org.name
			}));
	}, [organisationData, searchText]);

	// Find the currently selected workspace based on the selected profile
	const selectedWorkspace = organisationData.find((org) => org.orgID === selectedProfile?.orgID);

	return (
		<>
			<SelectSecondary
				showSearch={false}
				value={String(selectedWorkspace?.orgID)}
				onDropdownVisibleChange={(open) => {
					setSearchText('');
					if (!open) setIsHovered(false);
				}}
				onChange={(orgID) => dispatch(authActions.switchProfile(Number(orgID)))}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				style={{ borderRadius: borderRadiusLG }}
				fontWeight={fontWeightStrong}
				color={isHovered ? colorPrimary : undefined}
				dropdownStyle={{ width: 272, padding: `${padding}px ${paddingSM}px`, border: 'none' }}
				placeholder='Organisation'
				placement='bottomRight'
				optionRender={(option) => (
					<Flex
						justify='space-between'
						align='center'
						gap={paddingXS}
						style={{ padding: `${paddingXXS}px 0`, cursor: 'pointer' }}
					>
						{option.data.label}
						{option.value === String(selectedWorkspace?.orgID) && (
							<CheckCircleFilled style={{ color: colorPrimary }} />
						)}
					</Flex>
				)}
				dropdownRender={(menu) => (
					<Flex vertical gap={8} style={{ padding: paddingXXS }}>
						<Flex align='center' justify='center' vertical gap={16}>
							<Flex justify='space-between' align='center' style={{ width: '100%' }}>
								<Title level={5} style={{ margin: 0, color: colorTextLabel }}>
									Organisation
								</Title>
								<Tooltip
									title={
										isPermittedCreateOrg ? 'Create new organisation' : "You don't have permission"
									}
								>
									<Button
										disabled={!isPermittedCreateOrg}
										type='text'
										icon={<PlusOutlined />}
										onClick={() => setOpenCreateModal(true)}
									>
										New
									</Button>
								</Tooltip>
							</Flex>
							<Divider style={{ margin: '2px 0', borderColor: colorBorderSecondary }} />

							<Input
								placeholder='Search organisation'
								prefix={<SearchOutlined style={{ color: colorTextDescription }} />}
								value={searchText}
								onChange={(e) => setSearchText(e.target.value)}
								variant='filled'
							/>
						</Flex>

						<div
							style={{
								marginTop: paddingSM,
								maxHeight: 400,
								overflowY: 'auto',
								marginBottom: paddingXXS
							}}
						>
							{menu}
						</div>
					</Flex>
				)}
				options={filteredWorkspaces}
			/>

			<CreateOrganisationModal open={openCreateModal} setOpen={setOpenCreateModal}>
				<div />
			</CreateOrganisationModal>
		</>
	);
};

export default DropdownWorkspace;
